import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { getCurrentUser } from "./auth.js";
import { apiFetch } from "./api.js";

// Manažerské notifikace (undecided leads, ROI, CPL, milníky atd.) jsou určené
// pro vlastnické role. Trenér je nemá vidět ani fetchovat — má vlastní upozornění
// uvnitř trener portálu (Hero box, sekce Vyžaduje pozornost).
// CSM přidáno kvůli notifikacím o změně kontaktu klienta (Modul 2A1).
const ROLES_S_NOTIFIKACEMI = new Set(["owner", "manager", "sales", "csm", "nutritionist"]);

const AlertContext = createContext({
  alertCount: 0,
  setAlertCount: () => {},
  notifications: [],
  counts: { aktivní: 0, odloženo: 0, archivováno: 0 },
  loading: false,
  error: null,
  refresh: async () => {},
  sync: async () => {},
  archive: async () => {},
  snooze: async () => {},
  restore: async () => {},
});

export function AlertProvider({ children }) {
  // --- Notifikace z backendu (single source of truth) ---
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // counts a alertCount jsou DERIVED z notifications (vždy in sync, žádné race condition).
  // Bug 6.5.2026: dříve byly counts/alertCount samostatné state co se musely manuálně synchronizovat
  // po každé optimistické změně. setState callbacky se ale spouštějí asynchronně, takže `let movedFromActive`
  // closure variable často neodrážela skutečný počet → badge zvonečku se neaktualizoval.
  // Plošný fix: derivuju z notifications array, useEffect zajistí auto-sync.
  const [counts, setCounts] = useState({ aktivní: 0, odloženo: 0, archivováno: 0 });
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    const c = { aktivní: 0, odloženo: 0, archivováno: 0 };
    for (const n of notifications) {
      const stav = n.stav || "aktivní";
      c[stav] = (c[stav] || 0) + 1;
    }
    setCounts(c);
    setAlertCount(c["aktivní"] || 0);
  }, [notifications]);

  // refresh({ force: true }) — bypass server-side cache (např. po klient-edit)
  const refresh = useCallback(async (opts = {}) => {
    // Guard: jen role co mají oprávnění vidět manažerské notifikace volají endpoint.
    // Pro trenéra by se tím leakly KPI/leady ownera do jeho prohlížeče (i když UI je skryté).
    const user = getCurrentUser();
    if (!user || !ROLES_S_NOTIFIKACEMI.has(user.role)) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = opts.force ? "/api/notifications?force=1" : "/api/notifications";
      const res = await apiFetch(url);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setNotifications(data.notifications || []);
      // counts/alertCount auto-derive přes useEffect z notifications
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const sync = useCallback(async (alerts) => {
    try {
      await apiFetch("/api/notifications", {
        method: "POST",
        body: JSON.stringify({ action: "sync", alerts }),
      });
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  }, [refresh]);

  // Helper: optimisticky přesune notifikace do nového stavu (lokálně),
  // sníží/zvýší counts a alertCount, a na pozadí pošle backend volání.
  // Pokud backend selže, provede sync refresh aby se UI vrátilo do reality.
  const optimisticUpdate = useCallback(async (ids, newStav, payload) => {
    const idSet = new Set(Array.isArray(ids) ? ids : [ids]);
    // Update jen notifications — counts a alertCount se přepočtou autoamticky přes useEffect.
    setNotifications((prev) => prev.map((n) => {
      if (!idSet.has(n.id)) return n;
      return { ...n, stav: newStav };
    }));

    try {
      const res = await apiFetch("/api/notifications", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // BUG FIX 6.5.2026: ŽÁDNÝ refresh po úspěchu.
      // Backend má 30s cache na GET /api/notifications. Když refresh skončil
      // dřív než se cache invalidovala, dotáhl stale verzi a notifikace se
      // na chvíli vrátila do aktivních. Po druhém kliku už cache expirovala
      // a zmizela. Optimistický update už UI uvedl do správného stavu,
      // refresh není potřeba (a v tomto případě je škodlivý).
    } catch (e) {
      setError(e.message);
      refresh(); // při chybě synchronně synchronizovat
    }
  }, [refresh]);

  // 13.7.2026 fix "vracejících se chyb": u systémových chyb backend potřebuje
  // KLÍČ CHYBY (source+code+message) — id typu sys_N / sys_pgN se odvozuje ze
  // sheet_row_num, který se recykluje, takže Vyřízeno občas trefilo jiný řádek
  // a chyba se po refreshi vracela. Klíč dohledáme z aktuálních notifikací.
  const notificationsRef = useRef(notifications);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  const sysKlicFor = useCallback((id) => {
    if (!String(id).startsWith("sys_")) return null;
    const n = notificationsRef.current.find((x) => x.id === id);
    if (!n || !n._source) return null;
    return { source: n._source, code: n._code || "", message: n._rawMessage || "" };
  }, []);

  const archive = useCallback((id, poznamka) =>
    optimisticUpdate(id, "archivováno", { action: "archive", id, poznamka, sysKlic: sysKlicFor(id) || undefined }),
  [optimisticUpdate, sysKlicFor]);

  const snooze = useCallback((id, days) =>
    optimisticUpdate(id, "odloženo", { action: "snooze", id, days }),
  [optimisticUpdate]);

  const restore = useCallback((id) =>
    optimisticUpdate(id, "aktivní", { action: "restore", id, sysKlic: sysKlicFor(id) || undefined }),
  [optimisticUpdate, sysKlicFor]);

  const bulkArchive = useCallback((ids) => {
    if (!ids || ids.length === 0) return Promise.resolve();
    const sysKlice = {};
    for (const id of ids) {
      const k = sysKlicFor(id);
      if (k) sysKlice[id] = k;
    }
    return optimisticUpdate(ids, "archivováno", {
      action: "bulkArchive", ids,
      ...(Object.keys(sysKlice).length ? { sysKlice } : {}),
    });
  }, [optimisticUpdate, sysKlicFor]);

  const bulkSnooze = useCallback((ids, days) => {
    if (!ids || ids.length === 0) return Promise.resolve();
    return optimisticUpdate(ids, "odloženo", { action: "bulkSnooze", ids, days });
  }, [optimisticUpdate]);

  // Vytvoření nové notifikace (obecná, typicky milník / oslavný event).
  // Body: { id, typ: "milestone", klient: "", detail: "..." }
  // Dedupe ve 3 vrstvách: in-memory ref (proti race condition) + lokální state
  // + backend. Tím zabrání vytvoření více řádků v listu Notifikace při paralelních voláních.
  const sentNotificationIdsRef = useRef(new Set());
  const createNotification = useCallback(async ({ id, typ, klient, detail, datum_trialu }) => {
    if (!id) return;
    // 1. In-memory dedupe — nejrychlejší, odstíní opakovaná volání ze stejného mountu
    if (sentNotificationIdsRef.current.has(id)) return;
    sentNotificationIdsRef.current.add(id);

    // 2. Lokální state — skip pokud notifikace s daným id už existuje.
    // counts/alertCount se přepočtou auto přes useEffect.
    setNotifications((prev) => {
      if (prev.find((n) => n.id === id)) return prev;
      return [
        { id, typ, klient: klient || "", detail: detail || "", datum_trialu: datum_trialu || "",
          stav: "aktivní", odlozeno_do: "", vytvoreno: new Date().toISOString(), archivovano: "", poznamka: "" },
        ...prev,
      ];
    });

    // 3. Backend dedupe — backend checkuje existující ID před zápisem
    try {
      await apiFetch("/api/notifications", {
        method: "POST",
        body: JSON.stringify({ action: "create", id, typ, klient, detail, datum_trialu }),
      });
    } catch (e) {
      setError(e.message);
      // Při chybě uvolnit in-memory lock, aby šlo zkusit znovu
      sentNotificationIdsRef.current.delete(id);
      refresh();
    }
  }, [refresh]);

  // První načtení
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Instant přidání notifikace lokálně (např. po klient-edit dostane response.notification).
  // Přidá do state hned, plus background refresh za 1.5s aby se ujistil že je to v sheetu.
  const addLocal = useCallback((notif) => {
    if (!notif || !notif.id) return;
    const user = getCurrentUser();
    if (!user || !ROLES_S_NOTIFIKACEMI.has(user.role)) return;
    setNotifications((prev) => {
      // Dedup pro případ že refresh přijde dřív
      if (prev.some((n) => n.id === notif.id)) return prev;
      return [notif, ...prev];
    });
    // counts/alertCount se přepočtou auto přes useEffect.
    // Background reconcile s force aby se vyřešily případné conflicty
    setTimeout(() => { refresh({ force: true }).catch(() => {}); }, 1500);
  }, [refresh]);

  return (
    <AlertContext.Provider value={{
      alertCount, setAlertCount,
      notifications, counts, loading, error,
      refresh, sync, archive, snooze, restore, bulkArchive, bulkSnooze,
      createNotification, addLocal,
    }}>
      {children}
    </AlertContext.Provider>
  );
}

export function useAlertCount() {
  return useContext(AlertContext);
}

export function useNotifications() {
  return useContext(AlertContext);
}
